import * as React from "react";
import { createRoot } from "react-dom/client";
import App from "./components/App";
import { AppProviders } from "../providers/AppProviders";
import "../styles/globals.css";
import "../commands/commands"; // Import commands to register them

import { initializeTracking } from "../utils/analytics.utils";
import { toast } from "sonner";

/* global document, Office, module, require, HTMLElement */

interface ToastMessage {
  type: "info" | "error" | "success" | "warning";
  message: string;
  timestamp?: number;
}

const TOAST_STORAGE_KEY = "aila_command_toast";

const rootElement: HTMLElement | null = document.getElementById("container");
const root = rootElement ? createRoot(rootElement) : undefined;

function checkForToastMessages(): void {
  try {
    const stored = sessionStorage.getItem(TOAST_STORAGE_KEY);
    if (stored) {
      const message: ToastMessage = JSON.parse(stored);

      // Always clear regardless of whether we show it
      sessionStorage.removeItem(TOAST_STORAGE_KEY);

      // Only show if recent (within last 2 seconds)
      if (message.timestamp && Date.now() - message.timestamp < 2000) {
        switch (message.type) {
          case "error":
            toast.error(message.message);
            break;
          case "warning":
            toast.warning(message.message);
            break;
          case "info":
          case "success":
            toast(message.message);
            break;
          default:
            toast(message.message);
        }
      }
    }
  } catch (e) {
    // Ignore errors
  }
}

/* Render application after Office initializes */
Office.onReady(() => {
  initializeTracking();
  root?.render(
    <AppProviders>
      <App />
    </AppProviders>
  );

  // Check for toast messages from ribbon commands (shared runtime)
  // Check immediately and then periodically
  checkForToastMessages();
  const intervalId = setInterval(checkForToastMessages, 500);
  
  // Clean up interval after 10 seconds (commands should complete within that time)
  setTimeout(() => clearInterval(intervalId), 10000);
});

if ((module as any).hot) {
  (module as any).hot.accept("./components/App", () => {
    const NextApp = require("./components/App").default;
    root?.render(
      <AppProviders>
        <NextApp />
      </AppProviders>
    );
  });
}
